import { HttpErrorResponse } from "@angular/common/http";
import { TimeoutError } from "rxjs";

/**
 * The three-state machine `PlansDetailsComponent`/`NewPlanComponent` drive
 * their `GET v1/subscriptions/plans` load through (issue #600 AC-1/AC-4):
 * `"loading"` while the request is in flight (or on first mount), `"ready"`
 * once pricing has been applied, `"error"` on any failure (5xx, network, or
 * `PLANS_LOAD_TIMEOUT_MS` elapsing with no response) — rendering the in-page
 * error block with its "Try again" retry instead of the app's generic
 * "Something went wrong" page.
 */
export type PlansLoadState = "loading" | "ready" | "error";

/**
 * The `status` prop `pricing_plans_load_failed` reports (issue #600 AC-5,
 * contracts table): the real HTTP status for a non-network failure, the
 * literal string `"timeout"` when `PLANS_LOAD_TIMEOUT_MS` elapsed first,
 * `"invalid-shape"` for a 200 whose body is valid JSON but not a shape
 * `isValidPlansResponse()` accepts (QA #602 F-2 r2 — kept distinct from
 * `"network"` so the event says what actually happened instead of
 * conflating a bad body with a dropped connection), or `"network"` for
 * anything else (a `status: 0` `HttpErrorResponse`, or any other shape the
 * plans GET's error channel produced).
 */
export type PlansLoadFailureStatus = number | "timeout" | "invalid-shape" | "network";

/**
 * Passed to `classifyPlansLoadError()`/`reportPlansLoadFailure()` for a 200
 * whose body failed `isValidPlansResponse()` (QA #602 F-2 r2). This path
 * never throws or rejects — the bad body arrives in the subscription's
 * `next`, not `error` — so callers construct this marker themselves rather
 * than reporting the raw response, which `classifyPlansLoadError()` would
 * otherwise fall through to `"network"` for, indistinguishable from an
 * actually dropped connection.
 */
export class InvalidPlansShapeError {
  readonly isInvalidPlansShape = true as const;
}

/** Shared by both callers so the analytics payload's `status` never drifts between them. */
export function classifyPlansLoadError(error: unknown): PlansLoadFailureStatus {
  if (error instanceof InvalidPlansShapeError) {
    return "invalid-shape";
  }
  if (error instanceof TimeoutError) {
    return "timeout";
  }
  if (error instanceof HttpErrorResponse) {
    return error.status === 0 ? "network" : error.status;
  }
  return "network";
}

/**
 * The `pricing[identifier]` keys `PlansDetailsComponent`/`NewPlanComponent`'s
 * templates read directly (grep `pricing\[` in both `.html` files) — the
 * "plan shape the template actually reads" `isValidPlansResponse()` checks
 * a price for (QA #602 F-2 r2). An entry outside this set (e.g. the real
 * backend's `"freemium"`, whose `unit_amount` is legitimately `""` — no
 * template ever prices it) only has to carry a usable `identifier`, the same
 * as every other entry; its price is never validated because it is never
 * displayed.
 */
export const PRICED_PLAN_IDENTIFIERS: ReadonlySet<string> = new Set([
  "starter",
  "starter-yearly",
  "growth",
  "growth-yearly",
  "sales-pro-monthly",
  "sales-pro-yearly",
]);

/**
 * Issue #603 AC-1/AC-2/AC-7: a `PRICED_PLAN_IDENTIFIERS` member is "offered"
 * only when `v1/subscriptions/plans` actually carried it — `pricing[identifier]`
 * is `undefined`, checked with `in`/`=== undefined` rather than a falsy check
 * so a legitimately free plan (`unit_amount: 0`, `pricing[identifier] === 0`)
 * is never mistaken for a contact-sales-only one. An identifier outside
 * `PRICED_PLAN_IDENTIFIERS` (e.g. `"freemium"`) is always "priced" here —
 * this issue only concerns the six self-serve plans a template ever prices.
 */
export function isPlanPriced(pricing: Record<string, unknown>, identifier: string | undefined): boolean {
  if (!identifier || !PRICED_PLAN_IDENTIFIERS.has(identifier)) {
    return true;
  }
  return pricing[identifier] !== undefined;
}

/** A finite, renderable price — never `NaN`, `Infinity`, `undefined` or a numeric string (QA #602 F-2 r2). */
function isFiniteAmount(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/**
 * `entry` is shaped enough to destructure and key `pricing` with: a
 * non-null object carrying a non-empty string `identifier` (QA #602 F-2 r2).
 * This alone is what stops an array member that is `null`, a bare number, or
 * an object with no `identifier` from throwing inside `next`'s
 * `forEach(({identifier, unit_amount}) => ...)` (the original F-2 symptom)
 * or silently keying `pricing` with `pricing["undefined"]`.
 */
function hasPlanIdentifier(entry: unknown): entry is { identifier: string; unit_amount: unknown } {
  return (
    !!entry &&
    typeof entry === "object" &&
    typeof (entry as { identifier?: unknown }).identifier === "string" &&
    (entry as { identifier: string }).identifier.length > 0
  );
}

/**
 * `GET v1/subscriptions/plans` is only "ready" when `response.result` is a
 * non-empty array of entries every caller can safely destructure and price
 * (issue #600 AC-1/AC-4; QA #602 F-2, F-2 r2). Beyond the round-1 check
 * (`result` is an array), this now validates the shape of *every* entry —
 * the single place both callers' `next` handlers rely on before touching
 * `response.result` — closing the whole class QA's r2 residue pinned, not
 * just the one shape it strings out:
 *   - `result` missing, non-array, or `[]`: nothing to price, so every card
 *     would fall back to `displayedPrice()`'s `$0` — as unusable as a
 *     malformed body;
 *   - an entry that is `null`, a primitive, or otherwise not shaped like
 *     `{identifier, unit_amount}` (`[null]`, `[1,2]`): used to throw
 *     synchronously inside `next` — RxJS does not route an error thrown
 *     from `next` to the same subscription's `error` handler — stranding
 *     `plansLoadState` on `"loading"` forever;
 *   - an entry for one of the identifiers a template actually prices
 *     (`PRICED_PLAN_IDENTIFIERS`) whose `unit_amount` is missing or
 *     non-numeric: reached `"ready"` with `pricing[identifier]` `undefined`
 *     or `NaN`, rendering `$0/mo` cards and, on `/new-plan/:id`, an enabled
 *     "Due today $0" Confirm (QA #602 r2 M-01).
 * An entry for an identifier no template prices (e.g. `"freemium"`) is only
 * required to carry an `identifier`; its price is never checked, since no
 * template ever reads it.
 */
export function isValidPlansResponse(
  response: unknown
): response is { result: { identifier: string; unit_amount: number }[] } {
  if (!response || typeof response !== "object") {
    return false;
  }
  const result = (response as { result?: unknown }).result;
  if (!Array.isArray(result) || result.length === 0) {
    return false;
  }

  const amountByIdentifier = new Map<string, unknown>();
  for (const entry of result) {
    if (!hasPlanIdentifier(entry)) {
      return false;
    }
    amountByIdentifier.set(entry.identifier, entry.unit_amount);
  }

  for (const identifier of PRICED_PLAN_IDENTIFIERS) {
    if (amountByIdentifier.has(identifier) && !isFiniteAmount(amountByIdentifier.get(identifier))) {
      return false;
    }
  }
  return true;
}

/**
 * Moves focus after a Retry-triggered load settles (QA #602 F-3): the
 * reloaded content (`[data-testid="plans-ready-region"]`) on success, or
 * back onto "Try again" (`[data-testid="plans-load-retry"]`) if the retry
 * also failed — the two states are mutually exclusive (`*ngIf`), so whichever
 * one is present in `host` after the retry's change detection is the right
 * target. Never left on `<body>`. Not called on the initial mount, which has
 * nothing focus-worthy to steal focus from yet.
 */
export function focusPlansLoadResult(host: HTMLElement): void {
  const target =
    host.querySelector<HTMLElement>('[data-testid="plans-load-retry"]') ??
    host.querySelector<HTMLElement>('[data-testid="plans-ready-region"]');
  target?.focus();
}
